import isEmpty from 'lodash-es/isEmpty';

import { achBearerToken, isAchHost, maybeReauthOnAchUnauthorized } from './achAuth';
import { toAchApiPath } from './apiHostFor';
import { dispatchAchRequestError, HttpError, isWritesDisabled, parseProblem } from './httpError';

const getRequestOptions = (autoDetectContentType = false) => {
  const headers: Headers = new Headers();
  headers.append('Accept', 'application/json');
  headers.append('Accept-Language', 'en-US,en;q=0.9');
  headers.append('Cache-Control', 'no-cache');
  // Set the autoDetectContentType to true and the browser will automatically detect
  // eg. application/x-www-form-urlencoded or multipart/form-data for S3 uploads
  // Otherwise, the default is application/json.
  if (!autoDetectContentType) {
    headers.append('Content-Type', 'application/json;charset=UTF-8');
  }
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
    credentials: 'include',
  };

  return requestOptions;
};

const fetchHelper = (host: string, path?: string, requestOptions?: RequestInit): Promise<any | null> =>
  new Promise((resolve, reject) => {
    const fetchHelperAsync = async () => {
      try {
        // For an ACH host, rewrite the Ventura path (`/admin/...`) to ACH's
        // `/api/admin/v2/...` mount (design verify-item V2). This is what makes a
        // Tier-B domain flip config-only: a hook keeps sending its Ventura path,
        // and the chokepoint retargets it when the host resolves to ACH. Ventura
        // hosts are untouched. Idempotent for already-prefixed paths.
        const achHost = isAchHost(host);
        const fullUrl = host + (achHost ? toAchApiPath(path || '') : path || '');

        // Attach the ACH Bearer iff the target is an ACH host. Ventura/Rails
        // calls are untouched (they authenticate by session cookie). `token` is
        // hoisted so the 401 handler below knows whether a token was attached.
        let token: string | undefined;
        if (requestOptions && achHost) {
          token = achBearerToken();
          if (token) {
            const { headers } = requestOptions;
            if (headers instanceof Headers) {
              headers.set('Authorization', `Bearer ${token}`);
            } else {
              // Defensive: every current call path passes a Headers instance, but
              // support a plain-object headers bag too. Mutating the prepared
              // request options is this helper's job.
              // eslint-disable-next-line no-param-reassign
              requestOptions.headers = {
                ...(headers as Record<string, string> | undefined),
                Authorization: `Bearer ${token}`,
              };
            }
          }
        }

        const response = await fetch(fullUrl, requestOptions);

        // A Ventura 401 means the Devise session died → logout (existing
        // behaviour, unchanged). An ACH 401 must NEVER log the user out
        // (accepted posture, user ruling 2026-07-16): ACH is not the session
        // authority — Ventura is. A bad/expired sso_token on a flipped Tier-B
        // read falls through to the non-OK path below instead, so it surfaces
        // as an attributable ACH error (toast + HttpError) while the user's
        // healthy Rails session stays intact.
        if (response.status === 401 && !achHost) {
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }

        // An ACH 401 with a token attached: the sso_token went stale while the
        // Hub SSO session is (normally) still live. Auto-reauth ONCE through the
        // existing SSO flow, landing back on this exact page (ACH-2423 stopgap).
        // Still NEVER a logout — ACH is not the session authority. A recurring
        // 401 inside the guard window returns false here and falls through to the
        // attributable ACH error below, so it can't loop.
        if (response.status === 401 && achHost && (await maybeReauthOnAchUnauthorized(response.status, Boolean(token)))) {
          // Navigating away through SSO; leave the promise unsettled, matching
          // the Ventura 401 branch above (the page is unloading).
          return;
        }

        // For all other errors, reject with a typed HttpError. `message` stays
        // the raw body text (backward-compatible with existing catch sites);
        // `status` and the parsed RFC-7807 `problem` are additive.
        if (!response.ok) {
          const contentType = response.headers.get('Content-Type');
          const errorMessage = await response.text();
          const problem = parseProblem(contentType, errorMessage);

          // Surface ACH-host failures to the user (attributable during prod
          // parity testing); the writes-disabled 503 is a distinct subset.
          // Never fires for Ventura/Rails hosts — zero blast radius there.
          if (achHost) {
            dispatchAchRequestError({
              status: response.status,
              problem,
              message: errorMessage,
              url: fullUrl,
              writesDisabled: isWritesDisabled(response.status, problem),
            });
          }

          reject(new HttpError(response.status, errorMessage, problem));
          return;
        }

        let returnValue;
        if (response.headers.get('Content-Type')?.indexOf('application/json') >= 0) {
          returnValue = await response.json();
        } else {
          returnValue = await response.text();
        }

        resolve(returnValue);
      } catch (error) {
        reject(error);
      }
    };

    fetchHelperAsync();
  });

export const getRequestJSON = async (host: string, path?: string): Promise<any | null> => {
  const requestOptions = getRequestOptions();
  return fetchHelper(host, path, requestOptions);
};

const putPostHelper = (
  host: string,
  path: string,
  payload: object,
  file?: Blob,
  method: string = 'POST',
  options?: object
) => {
  const requestOptions = getRequestOptions();
  requestOptions.method = method;
  requestOptions.body = JSON.stringify(payload);
  return fetchHelper(host, path, { ...requestOptions, ...options });
};

export const postRequestJSON = async (
  host: string,
  path: string,
  payload: object,
  file?: Blob,
  options?: object
): Promise<any | null> => putPostHelper(host, path, payload, file, 'POST', options);

export const putRequestJSON = async (
  host: string,
  path: string,
  payload: object,
  file?: Blob,
  options?: object
): Promise<any | null> => putPostHelper(host, path, payload, file, 'PUT', options);

export const deleteRequestJSON = async (host: string, path?: string, options?: object): Promise<any | null> => {
  const requestOptions = getRequestOptions();
  requestOptions.method = 'DELETE';
  return fetchHelper(host, path, { ...requestOptions, ...options });
};

const uploadHelper = (
  host: string,
  path: string,
  payload: object,
  file: Blob,
  noCredentials?: boolean
): Promise<any | null> => {
  // Call getRequestOptions with true so that it will automatically use multipart/form-data
  const requestOptions = getRequestOptions(true);
  if (noCredentials) {
    // Must delete the credentials
    // because it will not work when the AllowedOrigins on S3 is set to '*'
    delete requestOptions.credentials;
  }
  requestOptions.method = 'POST';

  const formData = new FormData();

  if (!isEmpty(payload)) {
    const keys = Object.keys(payload);
    for (let i = 0; i < keys.length; i += 1) {
      const name = keys[i];
      formData.append(name, payload[name]);
    }
  }
  formData.append('content-type', file.type);
  formData.append('file', file);

  requestOptions.body = formData;

  return fetchHelper(host, path, requestOptions);
};

export const uploadFile = async (host: string, path: string, payload: object, file: Blob): Promise<any | null> =>
  uploadHelper(host, path, payload, file);

export const uploadToS3Request = async (
  host: string,
  path: string,
  payload: object,
  file: Blob,
  s3Key: string
): Promise<any | null> => uploadHelper(host, path, { ...payload, key: s3Key }, file, true);
